import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { config } from '../../../config';
import { LogLevelType } from '../../../enumerations/LogLevelType';
import { storeValueInLocal } from '../../../helpers/localStorageHelper';
import { getInstanceRepo } from '../../../helpers/persistence/common/repoHelper';

/*
 * Singleton class for Application Insights
 * Used for logging exceptions, events, traces, dependencies and page performance
 */
class AppInsightSingleton {
  static instance;

  event = null;

  appInsight = null;

  constructor() {
    if (AppInsightSingleton.instance) {
      return AppInsightSingleton.instance;
    }

    AppInsightSingleton.instance = this;
  }

  static getInstance() {
    if (!AppInsightSingleton.instance) {
      AppInsightSingleton.instance = new AppInsightSingleton();
    }

    return AppInsightSingleton.instance;
  }

  /**
   * @function setAppInsight sets the application insights instance
   * @param appInsightInstance instance of application insights
   */
  setAppInsight(appInsightInstance) {
    this.appInsight = appInsightInstance;
  }

  /**
   * @function getAppInsight get the application insights instance
   */
  getAppInsight() {
    return this.appInsight;
  }

  getExamType() {
    const instance = getInstanceRepo();
    if (instance?.module) {
      return instance?.module?.options?.isRealExam ||
        instance?.module?.options?.isRealExamMode
        ? 'Exam'
        : 'Prep';
    }
    return window.location.href.includes('/exam') ? 'Exam' : 'Prep';
  }

  /**
   * @function setEvent sets up the event to be logged
   *
   * Enabled only when REACT_APP_IS_SRE_ENABLED is true
   * @param {string} name name of the event to log
   */
  setEvent(name) {
    if (config.REACT_APP_IS_SRE_ENABLED === 'true') {
      this.event = { name, startTime: new Date() };
    }
  }

  /**
   *@function logException logs any exception raised in the application
   * @param {object} ex The exception to log
   * @param {object} source The source of the exception
   */
  logException(ex, source) {
    const ErrorEvents = ['resizeobserver', 'syntaxerror'];

    if (this.appInsight) {
      if (config.REACT_APP_ALLOW_SRE_CONSOLE === 'true') {
        console.log('SRE - LOGGING EXCEPTIONS', ex, source);
      }

      // Bug For Exceptions to be logged only once per 5 minutes
      // this is to avoid multiple logging of similar bugs
      if (
        ErrorEvents.filter(error => ex.message?.toLowerCase().includes(error))
          .length > 0
      ) {
        const lastTimeStampForException = localStorage.getItem(
          'timestampForExceptionBug',
        );
        if (lastTimeStampForException) {
          if (new Date() - new Date(lastTimeStampForException) > 300000) {
            storeValueInLocal('timestampForExceptionBug', new Date());
            this.appInsight.trackException(
              { error: ex },
              {
                source,
                name: this.event?.name ?? '',
                examType: this.getExamType(),
              },
            );
          }
        } else {
          storeValueInLocal('timestampForExceptionBug', new Date());
          this.appInsight.trackException(
            { error: ex },
            {
              source,
              name: this.event?.name ?? '',
              examType: this.getExamType(),
            },
          );
        }
      }
      // do not log script errors
      else if (!ex.message?.toLowerCase().includes('script error')) {
        this.appInsight.trackException(
          { error: ex },
          {
            source,
            name: this.event?.name ?? '',
            examType: this.getExamType(),
          },
        );
      }
    }
    if (source) {
      console.log(`${source}: ${ex}`);
    } else {
      console.log(ex);
    }
  }

  /**
   *@function log logs traces in the application insights
   * @param {LogLevelType} logLevel The log level type
   * @param {object} message The message or any object
   * @param  {any[]} restMessages The rest messages
   */
  log(logLevel, message, ...restMessages) {
    if (message && config.REACT_APP_SHOW_CONSOLE_LOG === 'true') {
      console.log(message, ...restMessages);
    }
    if (logLevel === LogLevelType.Error && this.appInsight) {
      this.appInsight.trackTrace(message, SeverityLevel.Error);
    }
  }

  /**
   * @function logEvent logs the event
   */
  logEvent() {
    if (this.appInsight && this.event) {
      if (config.REACT_APP_ALLOW_SRE_CONSOLE === 'true') {
        console.log('SRE - LOGGING CUSTOM EVENT', this.event.name);
      }
      this.appInsight.trackEvent({
        id: this.appInsight.context.telemetryTrace.traceID,
        name: this.event.name,
        timeStamp: this.event.startTime,
        properties: {
          examType: this.getExamType(),
        },
      });
    }
  }

  /**
   * @function logDependencyDataEvent logs the dependency data of the event
   * @param {boolean} success define if the API call successful
   * @param {number} responseCode status code of the API call
   * @param {string} target landing url after API success
   */
  logDependencyDataEvent(success, responseCode, target, errorMessage = '') {
    if (this.appInsight && this.event) {
      if (config.REACT_APP_ALLOW_SRE_CONSOLE === 'true') {
        console.log('SRE - LOGGING DEPENDENCY EVENT', this.event.name);
      }
      this.appInsight.trackDependencyData({
        id: this.appInsight.context.telemetryTrace.traceID,
        name: this.event.name,
        duration: new Date() - this.event.startTime,
        success,
        responseCode,
        properties: {
          landingUrl: target ?? window.location.href,
          examType: this.getExamType(),
          errorMessage,
        },
      });
    }
  }

  /**
   * @function logPagePerformanceEvent logs the page performance of the ongoing event
   * @param {string} name of the landing page after successful CTA
   */
  logPagePerformanceEvent(name) {
    if (this.appInsight && this.event) {
      if (config.REACT_APP_ALLOW_SRE_CONSOLE === 'true') {
        console.log('SRE - LOGGING PAGE VIEW EVENT', this.event.name);
      }
      this.appInsight.trackPageView({
        name: this.event.name,
        properties: {
          duration: new Date() - this.event.startTime,
          landingPage: name,
          examType: this.getExamType(),
        },
      });

      this.event = null;
    }
  }
}

const appInsightsInstance = AppInsightSingleton.getInstance();

export default appInsightsInstance;
